import { useState, useRef, useEffect } from 'react';

interface TooltipProps {
  children: React.ReactNode;
  content: React.ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
  delay?: number;
  className?: string;
  theme?: 'dark' | 'light';
  multiline?: boolean;
  tooltipClassName?: string;
}

const Tooltip = ({ 
  children, 
  content, 
  position = 'top', 
  delay = 500,
  className = '',
  theme = 'dark',
  multiline = false,
  tooltipClassName = ''
}: TooltipProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [timeoutId, setTimeoutId] = useState<number | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const showTooltip = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    const id = window.setTimeout(() => {
      setIsVisible(true);
    }, delay);
    setTimeoutId(id);
  };

  const hideTooltip = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }
    setIsVisible(false);
  };

  useEffect(() => {
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [timeoutId]);

  const getTooltipPosition = () => {
    switch (position) {
      case 'top':
        return {
          bottom: 'calc(100% + 8px)',
          left: '50%',
          transform: 'translateX(-50%)'
        };
      case 'bottom':
        return {
          top: 'calc(100% + 8px)',
          left: '50%',
          transform: 'translateX(-50%)'
        };
      case 'left':
        return {
          right: 'calc(100% + 8px)',
          top: '50%',
          transform: 'translateY(-50%)'
        };
      case 'right':
        return {
          left: 'calc(100% + 8px)',
          top: '50%',
          transform: 'translateY(-50%)'
        };
      default:
        return {};
    }
  };

  const getArrowPosition = () => {
    const topColorClass = theme === 'dark' ? 'border-t-gray-900' : 'border-t-white';
    const bottomColorClass = theme === 'dark' ? 'border-b-gray-900' : 'border-b-white';
    const leftColorClass = theme === 'dark' ? 'border-l-gray-900' : 'border-l-white';
    const rightColorClass = theme === 'dark' ? 'border-r-gray-900' : 'border-r-white';
    switch (position) {
      case 'top':
        return {
          className: `top-full left-1/2 transform -translate-x-1/2 border-l-transparent border-r-transparent border-b-transparent ${topColorClass}`,
          style: { marginTop: '-1px' }
        };
      case 'bottom':
        return {
          className: `bottom-full left-1/2 transform -translate-x-1/2 border-l-transparent border-r-transparent border-t-transparent ${bottomColorClass}`,
          style: { marginBottom: '-1px' }
        };
      case 'left':
        return {
          className: `left-full top-1/2 transform -translate-y-1/2 border-t-transparent border-b-transparent border-r-transparent ${leftColorClass}`,
          style: { marginLeft: '-1px' }
        };
      case 'right':
        return {
          className: `right-full top-1/2 transform -translate-y-1/2 border-t-transparent border-b-transparent border-l-transparent ${rightColorClass}`,
          style: { marginRight: '-1px' }
        };
      default:
        return { className: '', style: {} };
    }
  };

  return (
    <div 
      ref={triggerRef}
      className={`relative inline-block ${className}`}
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      onFocus={showTooltip}
      onBlur={hideTooltip}
    >
      {children}
      {isVisible && (
        <div
          ref={tooltipRef}
          className={`absolute z-50 text-sm rounded-lg shadow-xl ${multiline ? 'px-3 py-2 whitespace-normal max-w-xs leading-snug' : 'px-2 py-1 whitespace-nowrap'} ${theme === 'dark' ? 'text-white bg-gray-900/95' : 'text-gray-900 bg-white border border-gray-200'} ${tooltipClassName}`}
          style={{
            ...getTooltipPosition(),
            pointerEvents: 'none'
          }}
          role="tooltip"
        >
          {content}
          <div 
            className={`absolute w-0 h-0 border-4 ${getArrowPosition().className}`}
            style={getArrowPosition().style}
          />
        </div>
      )}
    </div>
  );
};

export default Tooltip;
